import React, { useCallback } from 'react';
import { Box, HealthPlanStatuses, ServiceConfig } from '@common-fe/common-fe';

import Permissions from '@/common/permissions';
import { useHasAccess } from '@/modules/core/hooks';
import ModuleWrapper from '@/modules/plan/ModuleWrapper';

import { HEALTH_PLAN_MODULES } from '../../HealthPlan.constants';
import { ActiveHealthPlanUpdateModal } from '../ActiveHealthPlanUpdateModal/ActiveHealthPlanUpdateModal';
import { useActiveHealthPlanUpdateModalStore } from '../ActiveHealthPlanUpdateModal/stores';

import ActivatePlan from './ActivateHealthPlan';
import FinalSetUpModal from './FinalSetupModal';
import { useHandleHealthPlanSetUp } from './hooks';
import SaveActiveHealthPlan from './SaveActiveHealthPlan';
import SaveHealthPlan from './SaveHealthPlan';
import ValidationNode from './ValidationNode';

interface Props {
  isEditMode?: boolean;
  planStatus?: HealthPlanStatuses;
  serviceConfigs: ServiceConfig[];
  hasUnselectedServiceTypes: boolean;
  isTemplateMode?: boolean;
  saveButtonRef?: React.RefObject<HTMLButtonElement | null>;
  activateButtonRef?: React.RefObject<HTMLButtonElement | null>;
  reviewButtonRef?: React.RefObject<HTMLButtonElement | null>;
  isEditableTemplate?: boolean;
}
const FinalSetup: React.FC<Props> = ({
  isEditMode,
  planStatus,
  serviceConfigs,
  hasUnselectedServiceTypes,
  isTemplateMode,
  saveButtonRef,
  activateButtonRef,
  reviewButtonRef,
  isEditableTemplate,
}) => {
  const {
    onSaveDraftHandler,
    onSubmitForReviewHandler,
    onUpdateHandler,
    onActivateHandler,

    errorMessages,
    successMessages,
    pendingMessages,

    activateErrorMessages,
    activateSuccessMessages,
    activatePedingMessages,

    onUpdateActiveHPHandler,
    updateActiveHP,
  } = useHandleHealthPlanSetUp(isTemplateMode);

  const handleSetModalState = useActiveHealthPlanUpdateModalStore((store) => store.handleSetState);
  const handleOpenModal = useCallback(() => {
    handleSetModalState({ modalVisible: true });
  }, [handleSetModalState]);

  const handleUpdateHealthPlan = useCallback(() => {
    handleSetModalState({ modalVisible: false });
    updateActiveHP();
  }, [handleSetModalState, updateActiveHP]);

  const isHasAccess = useHasAccess([
    {
      permission: Permissions.EDIT_DRAFT_PLAN,
    },
    {
      permission: Permissions.ACTIVATE_PLAN,
    },
  ]);
  if (!isHasAccess) {
    return null;
  }
  return (
    <ModuleWrapper anchorId={HEALTH_PLAN_MODULES.FINAL_SETUP} title="Final Setup" expandless={false}>
      <ValidationNode isTemplateMode={isTemplateMode} />
      <FinalSetUpModal
        mode={isTemplateMode ? 'Health Plan Template' : 'Health Plan'}
        handleSave={onSaveDraftHandler}
        handleSubmit={onSubmitForReviewHandler}
        handleUpdate={onUpdateHandler}
        handleActivate={onActivateHandler}
      />
      {planStatus === HealthPlanStatuses.ACTIVE || planStatus === HealthPlanStatuses.PENDING_ACTIVE ? (
        <Box pad="spacing8" round="container1Round" direction="row" align="stretch" flex="grow" width="50%">
          <SaveActiveHealthPlan
            saveButtonRef={saveButtonRef}
            errorMessages={activateErrorMessages}
            successMessages={activateSuccessMessages}
            pendingMessages={activatePedingMessages}
            isTemplateMode={isTemplateMode}
            viewMode={!isEditMode}
            onSave={() => onUpdateActiveHPHandler(handleOpenModal)}
          />
        </Box>
      ) : (
        <Box pad="spacing8" round="container1Round" direction="row" align="stretch" flex="grow">
          <SaveHealthPlan
            saveButtonRef={saveButtonRef}
            reviewButtonRef={reviewButtonRef}
            errorMessages={errorMessages}
            successMessages={successMessages}
            pendingMessages={pendingMessages}
            isTemplateMode={isTemplateMode}
            viewMode={!isEditMode}
            onSave={onSaveDraftHandler}
            onSubmit={onSubmitForReviewHandler}
          />
          <Box pad="spacing8" />

          <ActivatePlan
            activateButtonRef={activateButtonRef}
            errorMessages={activateErrorMessages}
            successMessages={activateSuccessMessages}
            pendingMessages={activatePedingMessages}
            isTemplateMode={isTemplateMode}
            hasUnselectedServiceTypes={hasUnselectedServiceTypes}
            viewMode={!isEditMode}
            onActivate={onActivateHandler}
            handleOpenWarnModal={handleOpenModal}
          />
        </Box>
      )}
      <ActiveHealthPlanUpdateModal
        onUpdate={handleUpdateHealthPlan}
        onActivate={onActivateHandler}
        isPlanNotActive={planStatus !== HealthPlanStatuses.ACTIVE && planStatus !== HealthPlanStatuses.PENDING_ACTIVE}
        isTemplateMode={isTemplateMode}
        serviceConfigs={serviceConfigs}
      />
    </ModuleWrapper>
  );
};

export default FinalSetup;
